import { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { useTheme } from "../components/ThemeProvider";
import usePermiso from "../hooks/usePermiso";
/** Plantilla A3 embebida en el bundle: evita fetch("/plantillas/...") que a menudo falla con "Failed to fetch" (red, extensiones, etc.). Mantener alineada con `public/plantillas/ficha_escaparate_a3.html`. */
import fichaEscaparateA3Html from "../Plantillas/ficha_escaparate_a3.html?raw";

/** Paleta UI escaparate: respeta tema claro/oscuro del CRM. */
function useEscaparatePalette() {
  const { theme } = useTheme();
  return useMemo(() => {
    const c = theme.colors;
    const dark = theme.name === "dark";
    if (dark) {
      return {
        bg: "#0d0d16",
        surface: "#13131f",
        surface2: "#1a1a2e",
        border: "rgba(255,255,255,0.08)",
        border2: "rgba(255,255,255,0.15)",
        text: "#f1f5f9",
        muted: "rgba(255,255,255,0.45)",
        faint: "rgba(255,255,255,0.20)",
        accent: "#2563eb",
        red: "#c8002a",
        amber: "#b45309",
        green: "#16a34a",
        inputFill: "rgba(255,255,255,0.07)",
        btnGhost: "rgba(255,255,255,0.05)",
        btnGhostInactive: "rgba(255,255,255,0.08)",
        selectBg: "rgba(255,255,255,0.06)",
        dragHighlight: "rgba(37,99,235,0.15)",
        sortThBg: "rgba(37,99,235,0.08)",
        multiCountBg: "rgba(37,99,235,0.20)",
        multiItemBg: "rgba(37,99,235,0.15)",
        filtroChip: "rgba(37,99,235,0.15)",
        filtroRefBg: "rgba(37,99,235,0.15)",
        escOuter: "#05050e",
        escRailTop: "linear-gradient(180deg,#555568 0%,#3a3a4e 40%,#252535 100%)",
        escRailTopShadow: "0 3px 10px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)",
        escLed: "linear-gradient(90deg,transparent 0%,rgba(180,200,255,0.25) 20%,rgba(220,235,255,0.45) 50%,rgba(180,200,255,0.25) 80%,transparent 100%)",
        escPanel: "linear-gradient(180deg,#0a0a16 0%,#080812 100%)",
        escSideBorder: "#252535",
        escCable: "linear-gradient(180deg,#6677aa 0%,#4455aa 25%,#6677aa 50%,#4455aa 75%,#6677aa 100%)",
        escRailBot: "linear-gradient(180deg,#1e1e2e 0%,#2a2a3e 50%,#3a3a4e 100%)",
        escRailBotShadow: "0 -2px 8px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.04)",
        panelMarco: "#0e0e18",
        panelShadowOn: "0 0 14px rgba(255,220,100,0.12),0 4px 12px rgba(0,0,0,0.7)",
        panelShadowOff: "0 2px 8px rgba(0,0,0,0.6)",
        emptyDrag: "rgba(37,99,235,0.18)",
        thumbBorder: "rgba(255,255,255,0.10)",
        modalBackdrop: "rgba(0,0,0,0.65)",
        modalShadow: "0 16px 48px rgba(0,0,0,0.8)",
        listRowFoundBg: "rgba(37,99,235,0.12)",
        listRowFoundBorder: "rgba(37,99,235,0.3)",
        zoomBorder: "rgba(255,255,255,0.18)",
        zoomShadow: "0 24px 64px rgba(0,0,0,0.92)",
        zoomBg: "#0e0e18",
        multiDropdownShadow: "0 8px 24px rgba(0,0,0,0.6)",
        filtroExclusivaBg: "rgba(245,158,11,0.12)",
        filtroCalifBg: "rgba(37,99,235,0.12)",
        cartelWrapBg: "#0e0e18",
      };
    }
    return {
      bg: c.bgApp,
      surface: c.bgCard,
      surface2: c.bgPanel,
      border: c.border,
      border2: c.sectionBorder,
      text: c.textPrimary,
      muted: c.textMuted,
      faint: c.textPlaceholder,
      accent: c.accentBlue,
      red: c.accentRed,
      amber: c.accentOrange,
      green: c.accentGreen,
      inputFill: c.bgInput,
      btnGhost: c.chipBg,
      btnGhostInactive: c.chipHoverBg,
      selectBg: c.chipBg,
      dragHighlight: c.accentBlueLight,
      sortThBg: c.accentBlueLight,
      multiCountBg: c.accentBlueLight,
      multiItemBg: c.accentBlueLight,
      filtroChip: c.accentBlueLight,
      filtroRefBg: c.accentBlueLight,
      escOuter: c.bgPanel,
      escRailTop: `linear-gradient(180deg, ${c.borderLight} 0%, ${c.border} 100%)`,
      escRailTopShadow: "0 1px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.7)",
      escLed: `linear-gradient(90deg,transparent 0%,${c.accentBlueLight} 25%,#b8c8dc 50%,${c.accentBlueLight} 75%,transparent 100%)`,
      escPanel: `linear-gradient(180deg, ${c.bgCard} 0%, ${c.chipBg} 100%)`,
      escSideBorder: c.border,
      escCable: "linear-gradient(180deg,#94a3b8 0%,#64748b 50%,#94a3b8 100%)",
      escRailBot: `linear-gradient(180deg, ${c.border} 0%, ${c.borderLight} 100%)`,
      escRailBotShadow: "0 -1px 4px rgba(0,0,0,0.08), inset 0 -1px 0 rgba(255,255,255,0.5)",
      panelMarco: "#0e0e18",
      panelShadowOn: "0 0 12px rgba(26,58,92,0.18),0 4px 12px rgba(0,0,0,0.12)",
      panelShadowOff: "0 2px 8px rgba(0,0,0,0.08)",
      emptyDrag: "rgba(26,58,92,0.12)",
      thumbBorder: c.border,
      modalBackdrop: "rgba(0,0,0,0.35)",
      modalShadow: c.cardShadow,
      listRowFoundBg: c.accentBlueLight,
      listRowFoundBorder: c.accentBlueBorder,
      zoomBorder: c.border,
      zoomShadow: "0 16px 40px rgba(0,0,0,0.18)",
      zoomBg: "#0e0e18",
      multiDropdownShadow: c.cardShadow,
      filtroExclusivaBg: "rgba(217,119,6,0.14)",
      filtroCalifBg: c.accentBlueLight,
      cartelWrapBg: "#0e0e18",
    };
  }, [theme]);
}

const BASE = window.location.hostname === "localhost"
  ? "http://localhost:4000"
  : "https://vitalcasa-crm-production.up.railway.app";
const resolveImg = f => {
  if (!f) return null;
  if (f.startsWith("http")) return f;
  return BASE + (f.startsWith("/") ? "" : "/uploads/") + f;
};

const norm = p => {
  const tipo = p.tipo_nombre || p.tipo || "";
  const loc  = p.poblacion_nombre || p.poblacion || "";
  let calificacion = null;
  const rawCal = p.calificacion;
  if (rawCal !== null && rawCal !== undefined && rawCal !== "") {
    const n = Number(rawCal);
    if (Number.isFinite(n)) calificacion = n;
  }
  return {
    ref:        p.referencia || "",
    tipo,
    loc,
    op:         p.operacion || "VENTA",
    precio:     p.precio_venta || p.precio || 0,
    m2:         p.m2_construidos || p.superficie_construida || 0,
    hab:        p.num_dormitorios || 0,
    ban:        p.num_banos || 0,
    exclusiva:  !!(p.exclusiva),
    calificacion,
    /* vendida: flag BD o pipeline explícito */
    vendida:    (p.disponible === false && p.publicar_web === false)
             || (p.etapa_pipeline === "vendido" || p.etapa_pipeline === "vendida"),
    foto:       resolveImg(p.fotos?.[0]),
    titulo:     p.titulo || (tipo ? tipo + " en " + loc : ""),
    descripcion: p.descripcion_es || p.descripcion || null,
    planta:     p.piso != null ? p.piso : null,
    eq_garaje:   p.eq_garaje  || false,
    eq_piscina:  p.eq_piscina_priv || p.piscina || false,
    eq_terraza:  p.eq_terraza || false,
    estado_cons: p.estado_conservacion || p.estado_conservacion_nombre || null,
  };
};

const fmtP = (v, op) =>
  op === "ALQUILER"
    ? Number(v).toLocaleString("es-ES") + " \u20ac/mes"
    : Number(v).toLocaleString("es-ES") + " \u20ac";

const EMPTY_PANELS = () =>
  Array.from({ length: 12 }, (_, i) => ({ id: i, prop: null, checked: false }));

/** Fusiona lo guardado con la plantilla de 12 huecos (por índice). */
function mergePanelsFromStorage(stored) {
  const base = EMPTY_PANELS();
  if (!Array.isArray(stored)) return base;
  for (let i = 0; i < 12; i++) {
    const s = stored[i];
    if (!s || typeof s !== "object") continue;
    base[i] = {
      id: i,
      prop: s.prop && typeof s.prop === "object" ? s.prop : null,
      checked: !!s.checked,
    };
  }
  return base;
}

/**
 * Antes se exigía p.exterior && p.interior; si interior faltaba o era null,
 * se tiraba todo el JSON y el usuario perdía también el escaparate exterior.
 */
function parseStoredEscaparatePanels(raw) {
  try {
    const p = JSON.parse(raw);
    const hasExt = Array.isArray(p.exterior);
    const hasInt = Array.isArray(p.interior);
    if (!hasExt && !hasInt) return null;
    return {
      exterior: hasExt ? mergePanelsFromStorage(p.exterior) : EMPTY_PANELS(),
      interior: hasInt ? mergePanelsFromStorage(p.interior) : EMPTY_PANELS(),
    };
  } catch (_) {
    return null;
  }
}

function normalizarPanelesDesdeServidor(server) {
  if (!server || typeof server !== "object") return null;
  if (!Array.isArray(server.exterior) || !Array.isArray(server.interior)) return null;
  return {
    exterior: mergePanelsFromStorage(server.exterior),
    interior: mergePanelsFromStorage(server.interior),
  };
}

function panelesTienenInmuebles(obj) {
  if (!obj?.exterior || !obj?.interior) return false;
  return [...obj.exterior, ...obj.interior].some((p) => p?.prop);
}

/* ── Inyector de datos en ficha_escaparate_a3.html (para impresion) ── */
function inyectarEscaparateNorm(doc, props) {
  const originalA3 = doc.querySelector(".a3");
  if (!originalA3) return;
  const parent = originalA3.parentNode;
  let lastInserted = null;

  props.forEach((p, idx) => {
    const esTipoAlquiler = p.op === "ALQUILER";
    const precioStr = p.precio
      ? Number(p.precio).toLocaleString("es-ES") + " \u20ac" + (esTipoAlquiler ? "/mes" : "")
      : "";

    const a3 = originalA3.cloneNode(true);

    const fotoImg = a3.querySelector(".foto img");
    if (fotoImg && p.foto) fotoImg.src = p.foto;

    const opBadge = a3.querySelector(".op-badge");
    if (opBadge) opBadge.textContent = p.op;

    const refTag = a3.querySelector(".ref-tag");
    if (refTag) refTag.textContent = "Ref. " + p.ref;

    const propType = a3.querySelector(".prop-type");
    if (propType) propType.textContent = p.tipo + " · " + p.loc;

    const propTitle = a3.querySelector(".prop-title");
    if (propTitle) propTitle.textContent = p.titulo || p.tipo + " en " + p.loc;

    const propDesc = a3.querySelector(".prop-desc");
    if (propDesc) {
      if (p.descripcion) propDesc.textContent = p.descripcion;
      else propDesc.style.display = "none";
    }

    const priceVal = a3.querySelector(".price-value");
    if (priceVal) {
      priceVal.textContent = precioStr || "";
      if (!precioStr) priceVal.style.display = "none";
    }

    const setSpec = (field, value) => {
      const el = a3.querySelector('[data-field="' + field + '"] .spec-value, [data-field="' + field + '"] .spec-tag-value');
      if (el) { if (value) el.textContent = value; else a3.querySelector('[data-field="' + field + '"]')?.remove(); }
    };
    setSpec("superficie",   p.m2    ? Math.round(p.m2) + " m\u00b2" : null);
    setSpec("habitaciones", p.hab   ? String(p.hab) : null);
    setSpec("banos",        p.ban   ? String(p.ban) : null);
    setSpec("planta",       p.planta != null ? p.planta + "\u00aa" : null);
    setSpec("garaje",       p.eq_garaje  ? "Incluido" : null);
    setSpec("terraza",      p.eq_terraza ? "S\u00ed" : null);
    setSpec("estado",       p.estado_cons || null);
    if (!p.eq_piscina) a3.querySelector('[data-field="piscina"]')?.remove();

    if (idx === 0) {
      parent.replaceChild(a3, originalA3);
      lastInserted = a3;
    } else {
      const br = doc.createElement("div");
      br.style.cssText = "page-break-before:always;height:0;overflow:hidden";
      const after = lastInserted.nextSibling;
      if (after) {
        parent.insertBefore(br, after);
        parent.insertBefore(a3, br.nextSibling);
      } else {
        parent.appendChild(br);
        parent.appendChild(a3);
      }
      lastInserted = a3;
    }
  });
}

/* ── Imprimir via iframe oculto ── */
async function imprimirEscaparate(props) {
  if (!props.length) return;
  const html = fichaEscaparateA3Html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelector(".screen-label")?.remove();
  doc.querySelector(".mode-switch")?.remove();

  inyectarEscaparateNorm(doc, props);

  const style = doc.createElement("style");
  style.textContent = `
    @page { size: 420mm 297mm; margin: 0; }
    @media print {
      html, body {
        background: #1a1a24 !important; margin: 0 !important; padding: 0 !important;
        display: block !important; overflow: visible !important;
      }
      img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      /* Una página A3 por cartel: sin fixed a pantalla completa (solo se imprimía el último) */
      .a3 {
        position: relative !important;
        top: auto !important; left: auto !important; right: auto !important; bottom: auto !important;
        width: 420mm !important;
        height: 297mm !important;
        background: transparent !important;
        box-shadow: none !important;
        overflow: hidden !important;
        transform: none !important;
        page-break-after: always !important;
        break-after: page !important;
      }
      .a3:last-of-type {
        page-break-after: auto !important;
        break-after: auto !important;
      }
      .foto {
        position: absolute !important;
        top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important;
        print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important;
        z-index: 0 !important;
      }
      .foto img { width: 100% !important; height: 100% !important; object-fit: cover !important; }
      .foto::after { print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; }
      .op-badge { top: 8mm !important; left: 8mm !important; }
      .bottom-panel, .op-badge, .ref-tag { z-index: 2 !important; position: absolute !important; }
    }`;
  doc.head.appendChild(style);

  const htmlFinal = "<!DOCTYPE html>" + doc.documentElement.outerHTML;
  const FRAME_ID = "__vc_print_iframe";
  let iframe = document.getElementById(FRAME_ID);
  if (iframe) iframe.remove();
  iframe = document.createElement("iframe");
  iframe.id = FRAME_ID;
  iframe.style.cssText =
    "position:fixed;left:-9999px;top:-9999px;width:420mm;" +
    `min-height:${297 * props.length}mm;height:auto;` +
    "border:none;opacity:0;pointer-events:none;z-index:-1;";
  document.body.appendChild(iframe);

  const frameDoc = iframe.contentDocument || iframe.contentWindow.document;
  frameDoc.open("text/html", "replace");
  frameDoc.write(htmlFinal);
  frameDoc.close();

  iframe.contentWindow.addEventListener("load", () => {
    iframe.contentWindow.addEventListener("afterprint", () => {
      setTimeout(() => { try { iframe.remove(); } catch (_) {} }, 500);
    }, { once: true });
    setTimeout(() => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) {}
    }, 350);
  }, { once: true });
}


/** Zoom 3× solo sobre referencia y precio en la vista al pasar el ratón (no sobre la foto entera). */
const ZOOM_REF_PRECIO_ESCAPARATE = 3;

/** A3 horizontal en px (~96 dpi), alineado con `ficha_escaparate_a3.html` (420mm × 297mm). */
const CARTEL_DESIGN_W = Math.round((420 * 96) / 25.4);
const CARTEL_DESIGN_H = Math.round((297 * 96) / 25.4);

/** Igual que `.foto::after` en la plantilla A3 (ver `public/plantillas/ficha_escaparate_a3.html` y bundle `?raw`). */
const CARTEL_FOTO_GRADIENT =
  "linear-gradient(to top, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.90) 10%, rgba(0,0,0,0.55) 16%, transparent 20%)";

const CARTEL_FONT = "'DM Sans', system-ui, sans-serif";

function CartelSpecIcon({ children }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width={22} height={22} style={{ flexShrink: 0, color: "#fff" }}>
      {children}
    </svg>
  );
}

/** Misma lógica de fila de specs que `inyectarEscaparateNorm` + plantilla HTML. */
function cartelSpecEntries(prop) {
  const out = [];
  if (prop.m2 > 0) {
    out.push({
      key: "sup",
      node: (
        <>
          <CartelSpecIcon>
            <rect x="3" y="3" width="18" height="18" rx="1" />
            <path d="M3 9h18M9 9v12" />
          </CartelSpecIcon>
          <span style={{ fontSize: 22, fontWeight: 700, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>
            {Math.round(prop.m2)}{" "}
            <span style={{ fontSize: 14, fontWeight: 400, color: "rgba(255,255,255,0.75)" }}>m²</span>
          </span>
        </>
      ),
    });
  }
  if (prop.hab > 0) {
    out.push({
      key: "hab",
      node: (
        <>
          <CartelSpecIcon>
            <path d="M3 20v-6a2 2 0 012-2h14a2 2 0 012 2v6" />
            <path d="M3 14V9a1 1 0 011-1h4v6" />
            <path d="M1 20h22" />
          </CartelSpecIcon>
          <span style={{ fontSize: 22, fontWeight: 700, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>{prop.hab}</span>
        </>
      ),
    });
  }
  if (prop.ban > 0) {
    out.push({
      key: "ban",
      node: (
        <>
          <CartelSpecIcon>
            <path d="M4 12h16v3a4 4 0 01-4 4H8a4 4 0 01-4-4v-3z" />
            <path d="M6 12V7a2 2 0 014 0" />
            <path d="M8 19v2M16 19v2" />
          </CartelSpecIcon>
          <span style={{ fontSize: 22, fontWeight: 700, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>{prop.ban}</span>
        </>
      ),
    });
  }
  if (prop.planta != null && prop.planta !== "") {
    out.push({
      key: "planta",
      node: (
        <>
          <CartelSpecIcon>
            <path d="M3 21h18M3 10h18M5 6l7-3 7 3" />
            <rect x="9" y="10" width="6" height="11" />
          </CartelSpecIcon>
          <span style={{ fontSize: 22, fontWeight: 700, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>{prop.planta}ª</span>
        </>
      ),
    });
  }
  const labeled = [
    prop.eq_garaje && { key: "garaje", label: "GARAJE", value: "Incluido" },
    prop.eq_terraza && { key: "terraza", label: "TERRAZA", value: "Sí" },
    prop.eq_piscina && { key: "piscina", label: "PISCINA", value: "Sí" },
    prop.estado_cons && { key: "estado", label: "ESTADO", value: String(prop.estado_cons) },
  ].filter(Boolean);
  labeled.forEach((x) => {
    out.push({
      key: x.key,
      labeled: true,
      label: x.label,
      value: x.value,
    });
  });
  return out;
}

/**
 * Cartel idéntico en maquetación a `ficha_escaparate_a3.html` (panel inferior en dos columnas, degradado, specs con iconos).
 * Escala al hueco de la miniatura o del zoom.
 */
function CartelInterior({ prop, zoomRefPrecio = false }) {
  const D = useEscaparatePalette();
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w < 1 || h < 1) return;
      /*
       * Contain: encajar todo el diseño A3 (ref arriba derecha, precio y specs abajo derecha).
       * Con "cover" (max), en paneles más altos que el A3 se recortaban los laterales y desaparecían ref/precio.
       */
      setScale(Math.min(w / CARTEL_DESIGN_W, h / CARTEL_DESIGN_H));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const precioStr = prop.precio
    ? fmtP(prop.precio, prop.op)
    : "";
  const specs = cartelSpecEntries(prop);
  const desc = prop.descripcion ? String(prop.descripcion).trim() : "";

  return (
    <div
      ref={wrapRef}
      style={{
        position: "absolute",
        inset: 0,
        overflow: zoomRefPrecio ? "visible" : "hidden",
        background: D.cartelWrapBg,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: CARTEL_DESIGN_W,
          height: CARTEL_DESIGN_H,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: "center center",
          fontFamily: CARTEL_FONT,
        }}
      >
          {prop.foto ? (
            <img src={prop.foto} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          ) : (
            <div style={{ position: "absolute", inset: 0, background: "#1a1a2e" }} />
          )}
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: CARTEL_FOTO_GRADIENT }} />

          <div style={{ position: "absolute", top: 0, left: 0, display: "flex", flexDirection: "row", alignItems: "stretch" }}>
            <div
              style={{
                background: "#c8002a",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 2.5,
                textTransform: "uppercase",
                padding: "10px 24px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {prop.op}
            </div>
            {prop.exclusiva && (
              <div
                style={{
                  background: D.amber,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  padding: "10px 16px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                EXCL.
              </div>
            )}
          </div>

          {/* Referencia muy visible (no estilo ficha): bloque propio, no el texto tenue de la plantilla impresa. */}
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 16,
              zIndex: 5,
              maxWidth: "min(52%, 420px)",
              padding: "8px 14px 10px",
              borderRadius: 8,
              background: "linear-gradient(135deg, rgba(37,99,235,0.96) 0%, rgba(29,78,216,0.98) 100%)",
              border: "2px solid rgba(255,255,255,0.55)",
              boxShadow: "0 6px 18px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.2)",
              transform: zoomRefPrecio ? `scale(${ZOOM_REF_PRECIO_ESCAPARATE})` : undefined,
              transformOrigin: zoomRefPrecio ? "top right" : undefined,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 2.2,
                color: "rgba(255,255,255,0.92)",
                textTransform: "uppercase",
                marginBottom: 3,
                lineHeight: 1,
              }}
            >
              Referencia
            </div>
            <div
              title={prop.ref || ""}
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: "#fff",
                letterSpacing: 0.5,
                lineHeight: 1.05,
                fontVariantNumeric: "tabular-nums",
                wordBreak: "break-all",
              }}
            >
              {prop.ref || "—"}
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 3,
              padding: "16px 32px 20px",
              display: "grid",
              gridTemplateColumns: "1fr auto",
              alignItems: "end",
              gap: 40,
              boxSizing: "border-box",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  letterSpacing: 2.5,
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.70)",
                  marginBottom: 6,
                }}
              >
                {prop.tipo}
                {prop.loc ? ` · ${prop.loc}` : ""}
              </div>
              <div
                style={{
                  fontSize: 38,
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1.15,
                  letterSpacing: -0.5,
                  marginBottom: 12,
                }}
              >
                {prop.titulo || (prop.tipo ? `${prop.tipo} en ${prop.loc || ""}`.trim() : "")}
              </div>
              {desc ? (
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.95)",
                    lineHeight: 1.5,
                    maxWidth: "100%",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {desc}
                </div>
              ) : null}
            </div>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12, flexShrink: 0 }}>
              {precioStr ? (
                <div
                  style={{
                    transform: zoomRefPrecio ? `scale(${ZOOM_REF_PRECIO_ESCAPARATE})` : undefined,
                    transformOrigin: zoomRefPrecio ? "bottom right" : undefined,
                    fontSize: 50,
                    fontWeight: 700,
                    color: "#fff",
                    letterSpacing: -2,
                    lineHeight: 1,
                    textAlign: "right",
                  }}
                >
                  {precioStr}
                </div>
              ) : null}
              {specs.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    border: "2px solid rgba(255,255,255,0.30)",
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "rgba(0,0,0,0.62)",
                  }}
                >
                  {specs.map((s, i) => (
                    <div
                      key={s.key}
                      style={
                        s.labeled
                          ? {
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "10px 20px",
                              borderRight: i < specs.length - 1 ? "1px solid rgba(255,255,255,0.18)" : "none",
                              gap: 3,
                            }
                          : {
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "12px 20px",
                              borderRight: i < specs.length - 1 ? "1px solid rgba(255,255,255,0.18)" : "none",
                            }
                      }
                    >
                      {s.labeled ? (
                        <>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 1.5,
                              textTransform: "uppercase",
                              color: "rgba(255,255,255,0.75)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s.label}
                          </span>
                          <span style={{ fontSize: 18, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>{s.value}</span>
                        </>
                      ) : (
                        s.node
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {prop.vendida && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.50)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <div
                style={{
                  background: D.red,
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 56,
                  letterSpacing: 4,
                  textTransform: "uppercase",
                  padding: "16px 48px",
                  transform: "rotate(-15deg)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                  border: "3px solid rgba(255,255,255,0.3)",
                  borderRadius: 8,
                }}
              >
                VENDIDA
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

/* ══ MiniCartel ══ */
function MiniCartel({ prop, dragOver, onZoomEnter, onZoomLeave }) {
  const D = useEscaparatePalette();
  const frameRef = useRef(null);
  if (!prop) return (
    <div style={{
      width:"100%", height:"100%", borderRadius:3,
      background: dragOver ? D.emptyDrag : D.btnGhost,
      border: "2px dashed " + (dragOver ? "#60a5fa" : D.border2),
      display:"flex", alignItems:"center", justifyContent:"center",
      color: dragOver ? "#93c5fd" : D.faint,
      fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:1, transition:"all .15s",
    }}>
      {dragOver ? "Soltar" : "Vacío"}
    </div>
  );
  return (
    <div
      style={{ width:"100%", height:"100%", position:"relative", borderRadius:3 }}
      onMouseEnter={() => {
        const el = frameRef.current;
        if (!el || !onZoomEnter) return;
        const r = el.getBoundingClientRect();
        onZoomEnter(prop, {
          width: r.width,
          height: r.height,
          cx: r.left + r.width / 2,
          cy: r.top + r.height / 2,
        });
      }}
      onMouseLeave={onZoomLeave}
    >
      <div
        ref={frameRef}
        data-escaparate-thumb-frame
        style={{
          width:"100%", height:"100%", position:"relative", overflow:"hidden", borderRadius:3,
          border: "1.5px solid " + (dragOver ? "#60a5fa" : D.thumbBorder),
          transition: "border-color 0.15s ease",
          boxSizing: "border-box",
        }}
      >
        <CartelInterior prop={prop} />
      </div>
    </div>
  );
}

/* ══ Panel escaparate con cable y clip ══ */
function EscPanel({ panel, onCheck, onEdit, onDrop, onZoomEnter, onZoomLeave, veAsignar, modAsignar, veImprimir }) {
  const D = useEscaparatePalette();
  const [over, setOver] = useState(false);
  const asignarTitle = !modAsignar ? "No tienes permiso para modificar la asignación de propiedades a paneles" : undefined;
  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", position:"relative", zIndex: 1 }}>
      {/* Clip superior */}
      <div style={{ height:10, display:"flex", justifyContent:"center", alignItems:"flex-end", flexShrink:0 }}>
        <div style={{
          width:18, height:9,
          background:"linear-gradient(180deg,#8899bb 0%,#5566aa 100%)",
          borderRadius:"3px 3px 0 0",
          boxShadow:"0 -1px 4px rgba(0,0,0,0.5)",
        }} />
      </div>
      {/* Marco del panel */}
      <div
        onDragOver={e => { if (!modAsignar) return; e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); if (!modAsignar) return; onDrop(panel.id, e.dataTransfer.getData("ref")); }}
        style={{
          flex:1, minHeight:0,
          background:D.panelMarco, borderRadius:"0 0 3px 3px", padding:0,
          overflow:"hidden",
          boxShadow: panel.prop
            ? D.panelShadowOn
            : D.panelShadowOff,
        }}
      >
        <MiniCartel
          prop={panel.prop}
          dragOver={over}
          onZoomEnter={panel.prop ? onZoomEnter : undefined}
          onZoomLeave={panel.prop ? onZoomLeave : undefined}
        />
      </div>
      {/* Controles */}
      <div style={{ display:"flex", alignItems:"center", gap:3, flexShrink:0, marginTop:3 }}>
        {veImprimir && (
          <input type="checkbox" checked={panel.checked} onChange={() => onCheck(panel.id)}
            style={{ accentColor:D.accent, cursor:"pointer", width:11, height:11, flexShrink:0 }} />
        )}
        {veAsignar && (
          <button onClick={() => modAsignar && onEdit(panel.id)} disabled={!modAsignar} title={asignarTitle} style={{
            flex:1, fontSize:8, padding:"2px 4px",
            background:D.btnGhost, border:"1px solid " + D.border2,
            borderRadius:3, color:D.muted, cursor: modAsignar ? "pointer" : "not-allowed",
            opacity: modAsignar ? 1 : 0.5,
            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", textAlign:"left",
          }}>{panel.prop ? panel.prop.ref : "＋ Añadir"}</button>
        )}
        {panel.prop && veAsignar && modAsignar && (
          <button onClick={() => onEdit(panel.id)} style={{
            background:"transparent", border:"none", color:D.muted, cursor:"pointer", fontSize:10, padding:"0 2px",
          }}>✎</button>
        )}
      </div>
    </div>
  );
}

/* ══ Multi-select dropdown ══ */
function MultiSel({ label, options, sel, setSel }) {
  const D = useEscaparatePalette();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const count = sel.length;
  const sorted = useMemo(() => [...options].sort((a,b) => a.localeCompare(b, "es")), [options]);
  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={() => setOpen(v => !v)} style={{
        padding:"3px 9px", borderRadius:6, fontSize:11, fontWeight:600,
        border:"1px solid " + (count > 0 ? D.accent : D.border2),
        background: count > 0 ? D.multiCountBg : D.btnGhost,
        color: count > 0 ? "#93c5fd" : D.muted, cursor:"pointer", whiteSpace:"nowrap",
      }}>
        {label}{count > 0 ? " (" + count + ")" : ""} ▾
      </button>
      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:200,
          background:D.surface2, border:"1px solid " + D.border2, borderRadius:8,
          padding:"6px 4px", minWidth:165, boxShadow:D.multiDropdownShadow,
          maxHeight:220, overflowY:"auto",
        }}>
          {sorted.map(o => (
            <label key={o} style={{
              display:"flex", alignItems:"center", gap:7, padding:"5px 10px",
              cursor:"pointer", borderRadius:5, fontSize:11, color:D.text,
              background: sel.includes(o) ? D.multiItemBg : "transparent",
            }}>
              <input type="checkbox" checked={sel.includes(o)}
                onChange={e => setSel(e.target.checked ? [...sel,o] : sel.filter(s => s !== o))}
                style={{ accentColor:D.accent }} />
              {o}
            </label>
          ))}
          {sel.length > 0 && (
            <button onClick={() => setSel([])} style={{
              width:"100%", marginTop:4, padding:"4px 0", fontSize:10,
              background:"transparent", border:"none", color:D.muted, cursor:"pointer",
            }}>✕ Limpiar</button>
          )}
        </div>
      )}
    </div>
  );
}

/* ══ Modal cambio de referencia ══ */
function ModalRef({ panelId, current, allProps, onClose, onSave }) {
  const D = useEscaparatePalette();
  const [val, setVal] = useState(current ? current.ref : "");
  const found = allProps.find(p => p.ref === val);
  return (
    <div style={{
      position:"fixed", inset:0, background:D.modalBackdrop,
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background:D.surface2, border:"1px solid " + D.border2, borderRadius:12,
        padding:"22px 26px", width:320, boxShadow:D.modalShadow,
      }}>
        <div style={{ color:D.text, fontWeight:700, fontSize:13, marginBottom:14 }}>
          Panel {panelId + 1} — Cambiar referencia
        </div>
        <input autoFocus value={val} onChange={e => setVal(e.target.value.toUpperCase())}
          placeholder="ej. VC-1042"
          style={{
            width:"100%", padding:"9px 12px", borderRadius:7, boxSizing:"border-box",
            background:D.inputFill, color:D.text, fontSize:13, outline:"none",
            border:"1px solid " + (found ? D.accent : D.border2),
          }} />
        {found && (
          <div style={{
            marginTop:8, padding:"8px 10px", borderRadius:6,
            background:D.listRowFoundBg, border:"1px solid " + D.listRowFoundBorder,
            display:"flex", alignItems:"center", gap:8,
          }}>
            {found.foto && <img src={found.foto} alt="" style={{ width:40,height:26,objectFit:"cover",borderRadius:3 }} />}
            <div>
              <div style={{ color:D.text, fontSize:12, fontWeight:600 }}>{found.tipo} en {found.loc}</div>
              <div style={{ color:D.muted, fontSize:11 }}>{fmtP(found.precio, found.op)}</div>
            </div>
          </div>
        )}
        <div style={{ display:"flex", gap:8, marginTop:16, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{
            padding:"7px 16px", borderRadius:6, border:"1px solid " + D.border2,
            background:"transparent", color:D.muted, cursor:"pointer", fontSize:12,
          }}>Cancelar</button>
          <button onClick={() => found && onSave(panelId, found)} disabled={!found}
            style={{
              padding:"7px 16px", borderRadius:6, border:"none", fontWeight:600, fontSize:12,
              background: found ? D.accent : D.btnGhostInactive,
              color: found ? "#fff" : D.faint, cursor: found ? "pointer" : "not-allowed",
            }}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

/* ══ Botón sidebar ══ */
function SBtn({ active, onClick, color, title, children }) {
  const D = useEscaparatePalette();
  return (
    <button onClick={onClick} title={title} style={{
      width:52, padding:"9px 4px",
      background: active ? (color || D.accent) : D.btnGhost,
      border:"1px solid " + (active ? (color || D.accent) : D.border),
      borderRadius:8, color: active ? "#fff" : D.muted,
      cursor:"pointer", fontSize:9, fontWeight:700, letterSpacing:0.3,
      textTransform:"uppercase", lineHeight:1.4, transition:"all .15s",
      display:"flex", flexDirection:"column", alignItems:"center", gap:2,
    }}>{children}</button>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════════════════════ */
export default function EscaparateManager() {
  const D = useEscaparatePalette();
  const { puede } = usePermiso();
  const veAsignar = puede("cfg_esc_asignar", "ver");
  const modAsignar = puede("cfg_esc_asignar", "modificar");
  const veVistaExt = puede("cfg_esc_vista_ext", "ver");
  const veVistaInt = puede("cfg_esc_vista_int", "ver");
  const veImprimir = puede("cfg_esc_imprimir", "ver");
  const modGuardar = puede("cfg_esc_guardar", "modificar");
  /* ── datos ── */
  const [propiedades, setPropiedades] = useState([]);
  const [cargando,    setCargando]    = useState(false);
  const [buscar,      setBuscar]      = useState("");
  const [totalPags,   setTotalPags]   = useState(1);
  const [pagina,      setPagina]      = useState(1);
  const debRef = useRef(null);

  /* ── filtros ── */
  const [filtroOp,    setFiltroOp]    = useState("todos");
  /** "todas" | "si" | "no" */
  const [filtroExclusiva, setFiltroExclusiva] = useState("todas");
  /** "" | "sin" | "0" | "1" | "2" | "3" */
  const [filtroCalificacion, setFiltroCalificacion] = useState("");
  const [filtroRef,   setFiltroRef]   = useState("");
  const [filtroTipos, setFiltroTipos] = useState([]);
  const [filtroPobs,  setFiltroPobs]  = useState([]);
  const [filtroMax,   setFiltroMax]   = useState(0);

  /* ── ordenación tabla ── */
  const [sortCol, setSortCol] = useState("ref");
  const [sortDir, setSortDir] = useState("asc");

  /* ── escaparate (localStorage + servidor único para toda la oficina) ── */
  const [escaparateSyncReady, setEscaparateSyncReady] = useState(false);
  const saveEscaparateTimerRef = useRef(null);
  const [vista,   setVista]   = useState(() => (veVistaExt ? "exterior" : "interior"));
  const [paneles, setPaneles] = useState(() => {
    try {
      const saved = localStorage.getItem("vc_escaparate_panels");
      if (saved) {
        const parsed = parseStoredEscaparatePanels(saved);
        if (parsed) return parsed;
      }
    } catch (_) {}
    return { exterior: EMPTY_PANELS(), interior: EMPTY_PANELS() };
  });
  const [modal,  setModal]  = useState(null);
  const [drag,   setDrag]   = useState(null);
  const [printing, setPrinting] = useState(false);
  /** Vista ampliada: mismo encuadre ×3, anclada al centro de la miniatura y desplazada si hace falta para no recortar. */
  const [zoomPreview, setZoomPreview] = useState(null); // { prop, w, h, cx, cy }
  const [zoomViewportRev, setZoomViewportRev] = useState(0);
  const hideZoomTimerRef = useRef(null);

  const clearHideZoom = useCallback(() => {
    if (hideZoomTimerRef.current) {
      clearTimeout(hideZoomTimerRef.current);
      hideZoomTimerRef.current = null;
    }
  }, []);

  /** Tiempo para cruzar huecos entre paneles sin cerrar el zoom (el popup no captura el puntero). */
  const scheduleHideZoom = useCallback(() => {
    clearHideZoom();
    hideZoomTimerRef.current = setTimeout(() => setZoomPreview(null), 900);
  }, [clearHideZoom]);

  const openZoomPreview = useCallback((p, dims) => {
    if (!p || !dims?.width || !dims?.height || !Number.isFinite(dims.cx) || !Number.isFinite(dims.cy)) return;
    clearHideZoom();
    setZoomPreview({ prop: p, w: dims.width, h: dims.height, cx: dims.cx, cy: dims.cy });
  }, [clearHideZoom]);

  useEffect(() => {
    if (!zoomPreview) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        clearHideZoom();
        setZoomPreview(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomPreview, clearHideZoom]);

  useEffect(() => {
    if (!zoomPreview) return;
    const onResize = () => setZoomViewportRev((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [zoomPreview]);

  /* Persistir paneles en local (caché) */
  useEffect(() => {
    try { localStorage.setItem("vc_escaparate_panels", JSON.stringify(paneles)); } catch (_) {}
  }, [paneles]);

  /* Si la vista actual queda oculta por permisos, saltar a la otra disponible. */
  useEffect(() => {
    if (vista === "exterior" && !veVistaExt && veVistaInt) setVista("interior");
    if (vista === "interior" && !veVistaInt && veVistaExt) setVista("exterior");
  }, [vista, veVistaExt, veVistaInt]);

  /* Cargar escaparate del servidor (misma cuenta en otro PC) o migrar local → servidor */
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get("/escaparate/panels");
        if (cancel) return;
        const server = res.data?.data;
        const normalizado = normalizarPanelesDesdeServidor(server);
        if (normalizado && panelesTienenInmuebles(normalizado)) {
          setPaneles(normalizado);
          try {
            localStorage.setItem("vc_escaparate_panels", JSON.stringify(normalizado));
          } catch (_) {}
        } else if (modGuardar) {
          const raw = localStorage.getItem("vc_escaparate_panels");
          const localParsed = raw ? parseStoredEscaparatePanels(raw) : null;
          if (localParsed && panelesTienenInmuebles(localParsed)) {
            try {
              await api.put("/escaparate/panels", localParsed);
            } catch (e) {
              console.error("Migrar escaparate a servidor:", e);
            }
          }
        }
      } catch (e) {
        console.error("Cargar escaparate desde servidor:", e);
      } finally {
        if (!cancel) setEscaparateSyncReady(true);
      }
    })();
    return () => { cancel = true; };
  }, []);

  /* Guardar escaparate en servidor al editar */
  useEffect(() => {
    if (!escaparateSyncReady || !modGuardar) return;
    if (saveEscaparateTimerRef.current) clearTimeout(saveEscaparateTimerRef.current);
    saveEscaparateTimerRef.current = setTimeout(() => {
      api.put("/escaparate/panels", paneles).catch((e) => {
        console.error("Guardar escaparate en servidor:", e);
      });
    }, 700);
    return () => {
      if (saveEscaparateTimerRef.current) clearTimeout(saveEscaparateTimerRef.current);
    };
  }, [paneles, escaparateSyncReady, modGuardar]);

  /* ── fetch ── */
  const cargar = useCallback(async (p = 1, txt = "") => {
    setCargando(true);
    try {
      const q = new URLSearchParams({ page: String(p), perPage: "2000" });
      if (txt) q.set("buscar", txt);
      const res = await api.get("/propiedades?" + q.toString());
      const raw = res.data?.data || res.data || [];
      setPropiedades(raw.map(norm));
      setTotalPags(res.data?.totalPages || 1);
      setPagina(p);
    } catch (e) { console.error(e); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(1, ""); }, [cargar]);

  const refsEnPaneles = useMemo(
    () => [...new Set([...paneles.exterior, ...paneles.interior].filter((p) => p.prop?.ref).map((p) => p.prop.ref))].filter(Boolean),
    [paneles]
  );

  /* Refrescar datos de inmuebles en paneles (precio, m², vendida…) cuando cambian las refs o tras sync.
     La clave en texto y el early return de setPaneles cortan el bucle: al escribir
     siempre un objeto nuevo, el efecto se volvía a disparar a sí mismo y la página
     bombardeaba /api/propiedades sin parar (visto en producción, con la API a 2s). */
  const refsEnPanelesKey = refsEnPaneles.join("|");
  useEffect(() => {
    if (!escaparateSyncReady || !refsEnPanelesKey) return;
    refsEnPanelesKey.split("|").filter(Boolean).forEach((ref) => {
      (async () => {
        try {
          const res = await api.get("/propiedades?buscar=" + encodeURIComponent(ref) + "&perPage=5&cartera=todas");
          const raw = res.data?.data || res.data || [];
          const refU = String(ref || "").toUpperCase();
          const found = raw.find((r) => String(r.referencia || "").toUpperCase() === refU);
          if (!found) return;
          const fresh = norm(found);
          setPaneles((prev) => {
            let cambio = false;
            const upd = (panels) =>
              panels.map((p) => {
                if (p.prop?.ref !== ref) return p;
                if (JSON.stringify(p.prop) === JSON.stringify(fresh)) return p;
                cambio = true;
                return { ...p, prop: fresh };
              });
            const exterior = upd(prev.exterior);
            const interior = upd(prev.interior);
            return cambio ? { exterior, interior } : prev;
          });
        } catch (_) {}
      })();
    });
  }, [escaparateSyncReady, refsEnPanelesKey]);

  const onBuscar = v => {
    setBuscar(v);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => cargar(1, v), 350);
  };

  /* ── listas para filtros (ordenadas) ── */
  const tipos = useMemo(() => [...new Set(propiedades.map(p => p.tipo).filter(Boolean))].sort((a,b) => a.localeCompare(b,"es")), [propiedades]);
  const pobs  = useMemo(() => [...new Set(propiedades.map(p => p.loc).filter(Boolean))].sort((a,b)  => a.localeCompare(b,"es")), [propiedades]);

  /* ── filtrado + ordenación ── */
  const filtered = useMemo(() => {
    let list = propiedades.filter(p => {
      if (filtroOp !== "todos" && p.op !== filtroOp.toUpperCase()) return false;
      if (filtroExclusiva === "si" && !p.exclusiva) return false;
      if (filtroExclusiva === "no" && p.exclusiva) return false;
      if (filtroCalificacion === "sin") {
        if (p.calificacion !== null) return false;
      } else if (filtroCalificacion !== "") {
        if (Number(p.calificacion) !== Number(filtroCalificacion)) return false;
      }
      if (filtroRef && !p.ref.toLowerCase().includes(filtroRef.toLowerCase())) return false;
      if (filtroTipos.length && !filtroTipos.includes(p.tipo)) return false;
      if (filtroPobs.length  && !filtroPobs.includes(p.loc))   return false;
      if (filtroMax > 0 && p.precio > filtroMax) return false;
      return true;
    });

    list = [...list].sort((a, b) => {
      let va = a[sortCol] ?? "";
      let vb = b[sortCol] ?? "";
      if (sortCol === "precio") { va = Number(va); vb = Number(vb); return sortDir === "asc" ? va - vb : vb - va; }
      return sortDir === "asc" ? String(va).localeCompare(String(vb),"es") : String(vb).localeCompare(String(va),"es");
    });
    return list;
  }, [propiedades, filtroOp, filtroExclusiva, filtroCalificacion, filtroRef, filtroTipos, filtroPobs, filtroMax, sortCol, sortDir]);

  const toggleSort = col => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  /* ── helpers paneles ── */
  const cur      = paneles[vista];
  const selCount = cur.filter(p => p.checked).length;
  const setPan   = fn => setPaneles(prev => ({ ...prev, [vista]: fn(prev[vista]) }));

  const handleCheck = id => setPan(ps => ps.map(p => p.id === id ? { ...p, checked:!p.checked } : p));
  const handleDrop  = (id, ref) => {
    const found = propiedades.find(p => p.ref === ref);
    if (!found) return;
    setPan(ps => ps.map(p => p.id === id ? { ...p, prop:found, checked:true } : p));
  };
  const handleSave = (id, found) => {
    setPan(ps => ps.map(p => p.id === id ? { ...p, prop:found, checked:true } : p));
    setModal(null);
  };
  const selAll  = () => setPan(ps => ps.map(p => ({ ...p, checked: !!p.prop })));
  const selNone = () => setPan(ps => ps.map(p => ({ ...p, checked: false })));

  const handlePrint = async () => {
    const props = cur.filter(p => p.checked && p.prop).map(p => p.prop);
    if (!props.length) return;
    setPrinting(true);
    try { await imprimirEscaparate(props); } catch (e) { console.error(e); await dialogs.alert("Error al imprimir: " + e.message); }
    finally { setPrinting(false); }
  };

  /* ── UI helpers ── */
  const maxPrecios = [0, 100000, 200000, 300000, 500000, 1000000, 2000000];
  const selStyle = {
    background: D.selectBg, border:"1px solid " + D.border2,
    color:D.text, padding:"4px 8px", borderRadius:6, fontSize:11, outline:"none", cursor:"pointer",
  };
  const thStyle = (col) => ({
    padding:"10px 8px", textAlign:col === "precio" ? "right" : "left",
    fontWeight:600,
    fontSize: col === "ref" ? 10 : 11,
    color: sortCol === col ? "#93c5fd" : D.muted,
    textTransform:"uppercase", borderBottom:"1px solid " + D.border, whiteSpace:"nowrap",
    cursor:"pointer", userSelect:"none",
    background: sortCol === col ? D.sortThBg : "transparent",
  });
  const sortIcon = col => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : " ⇅";

  const zoomPortal = useMemo(() => {
    if (!zoomPreview) return null;
    void zoomViewportRev;
    const margin = 16;
    const zw = zoomPreview.w;
    const zh = zoomPreview.h;
    const vw = typeof window !== "undefined" ? window.innerWidth : zw + margin * 2;
    const vh = typeof window !== "undefined" ? window.innerHeight : zh + margin * 2;
    const fit = Math.min(1, (vw - 2 * margin) / zw, (vh - 2 * margin) / zh);
    const dispW = zw * fit;
    const dispH = zh * fit;
    const { cx, cy } = zoomPreview;
    let left = cx - dispW / 2;
    let top = cy - dispH / 2;
    const minL = margin;
    const minT = margin;
    const maxL = vw - margin - dispW;
    const maxT = vh - margin - dispH;
    left = Math.min(Math.max(left, minL), Math.max(minL, maxL));
    top = Math.min(Math.max(top, minT), Math.max(minT, maxT));
    const br = 3;
    const bd = 1.5;
    return createPortal(
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10050,
          /* No capturar el puntero: el zoom es solo visual; así se puede ir a otra miniatura en línea recta. */
          pointerEvents: "none",
        }}
      >
        <div
          data-escaparate-zoom-preview
          style={{
            pointerEvents: "none",
            position: "fixed",
            left,
            top,
            width: dispW,
            height: dispH,
            overflow: "visible",
            zIndex: 10051,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: zw,
              height: zh,
              transform: `scale(${fit})`,
              transformOrigin: "top left",
              borderRadius: br,
              overflow: "visible",
              border: `${bd}px solid ${D.zoomBorder}`,
              boxSizing: "border-box",
              boxShadow: D.zoomShadow,
              background: D.zoomBg,
            }}
          >
            <CartelInterior prop={zoomPreview.prop} zoomRefPrecio />
          </div>
        </div>
      </div>,
      document.body
    );
  }, [zoomPreview, zoomViewportRev, D]);

  return (
    <div style={{
      display:"flex", height:"calc(100vh - 56px)", overflow:"hidden",
      background:D.bg, fontFamily:"system-ui,sans-serif", color:D.text,
    }}>

      {/* ══════════════ IZQUIERDA — tabla ══════════════ */}
      {veAsignar && (
      <div style={{
        width:"42%", borderRight:"1px solid " + D.border,
        display:"flex", flexDirection:"column", overflow:"hidden", background:D.surface,
      }}>
        {/* Cabecera filtros */}
        <div style={{
          padding:"11px 14px 9px", borderBottom:"1px solid " + D.border,
          background:D.surface2, flexShrink:0,
        }}>
          <div style={{ display:"flex", alignItems:"center", marginBottom:9 }}>
            <span style={{ fontWeight:700, fontSize:13, flex:1 }}>
              Propiedades
              <span style={{ fontSize:11, color:D.muted, fontWeight:400, marginLeft:6 }}>— arrastra hasta un panel</span>
            </span>
            {cargando && <span style={{ fontSize:10, color:D.muted }}>Cargando…</span>}
          </div>
          {/* Buscador */}
          <input value={buscar} onChange={e => onBuscar(e.target.value)}
            placeholder="🔍  Buscar…"
            style={{
              width:"100%", padding:"7px 10px", borderRadius:7, boxSizing:"border-box",
              background:D.inputFill, border:"1px solid " + D.border2,
              color:D.text, fontSize:12, outline:"none", marginBottom:8,
            }} />
          {/* Fila 1: operación */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:5, alignItems:"center", marginBottom:6 }}>
            {["todos","venta","alquiler"].map(f => (
              <button key={f} onClick={() => setFiltroOp(f)} style={{
                padding:"3px 9px", borderRadius:20, fontSize:10, fontWeight:700,
                border:"1px solid " + (filtroOp === f ? D.accent : D.border2),
                background: filtroOp === f ? D.accent : "transparent",
                color: filtroOp === f ? "#fff" : D.muted, cursor:"pointer", textTransform:"capitalize",
              }}>{f === "todos" ? "Todos" : f === "venta" ? "Venta" : "Alquiler"}</button>
            ))}
          </div>
          {/* Fila 2: Ref + Tipo + Población + Precio + Exclusiva + Calificación */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:5, alignItems:"center" }}>
            <input value={filtroRef} onChange={e => setFiltroRef(e.target.value.toUpperCase())}
              placeholder="Ref…"
              style={{
                width:80, padding:"4px 8px", borderRadius:6, fontSize:11, outline:"none",
                background: filtroRef ? D.filtroRefBg : D.selectBg,
                border:"1px solid " + (filtroRef ? D.accent : D.border2),
                color:D.text,
              }} />
            <MultiSel label="Tipo"      options={tipos} sel={filtroTipos} setSel={setFiltroTipos} />
            <MultiSel label="Población" options={pobs}  sel={filtroPobs}  setSel={setFiltroPobs} />
            <select value={filtroMax} onChange={e => setFiltroMax(Number(e.target.value))} style={selStyle}>
              {maxPrecios.map(v => (
                <option key={v} value={v}>{v === 0 ? "Precio: todos" : "Hasta " + v.toLocaleString("es-ES") + " \u20ac"}</option>
              ))}
            </select>
            <select
              value={filtroExclusiva}
              onChange={e => setFiltroExclusiva(e.target.value)}
              style={{
                ...selStyle,
                border: "1px solid " + (filtroExclusiva !== "todas" ? D.amber : D.border2),
                background: filtroExclusiva !== "todas" ? D.filtroExclusivaBg : selStyle.background,
              }}
            >
              <option value="todas">Exclusiva: todas</option>
              <option value="si">⭐ Solo exclusivas</option>
              <option value="no">Sin exclusiva</option>
            </select>
            <select
              value={filtroCalificacion}
              onChange={e => setFiltroCalificacion(e.target.value)}
              style={{
                ...selStyle,
                border: "1px solid " + (filtroCalificacion ? D.accent : D.border2),
                background: filtroCalificacion ? D.filtroCalifBg : selStyle.background,
              }}
            >
              <option value="">Calificación: todas</option>
              <option value="sin">Sin calificar</option>
              <option value="0">0★ Chollo</option>
              <option value="1">1★ Adecuado</option>
              <option value="2">2★ Caro</option>
              <option value="3">3★ Fuera mercado</option>
            </select>
          </div>
        </div>

        {/* Tabla */}
        <div style={{ flex:1, overflowY:"auto", overflowX:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
            {/* Menos ancho en Ref+foto para que Tipo / Localidad / Precio ganen espacio (Precio suele ser lo más largo). */}
            <colgroup>
              <col style={{ width:"43%" }} />
              <col style={{ width:"17%" }} />
              <col style={{ width:"20%" }} />
              <col style={{ width:"20%" }} />
            </colgroup>
            <thead>
              <tr style={{ background:D.surface2, position:"sticky", top:0, zIndex:1 }}>
                <th onClick={() => toggleSort("ref")}     style={thStyle("ref")}>Ref.{sortIcon("ref")}</th>
                <th onClick={() => toggleSort("tipo")}    style={thStyle("tipo")}>Tipo{sortIcon("tipo")}</th>
                <th onClick={() => toggleSort("loc")}     style={thStyle("loc")}>Localidad{sortIcon("loc")}</th>
                <th onClick={() => toggleSort("precio")}  style={{ ...thStyle("precio"), textAlign:"right" }}>Precio{sortIcon("precio")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.ref} draggable={modAsignar}
                  title={!modAsignar ? "No tienes permiso para modificar la asignación de propiedades a paneles" : undefined}
                  onDragStart={e => { if (!modAsignar) return; setDrag(p.ref); e.dataTransfer.setData("ref", p.ref); }}
                  onDragEnd={() => setDrag(null)}
                  style={{
                    borderBottom:"1px solid " + D.border,
                    background: drag === p.ref ? D.dragHighlight : i % 2 === 0 ? D.surface : D.surface2,
                    cursor: modAsignar ? "grab" : "default", opacity: p.vendida ? 0.45 : (modAsignar ? 1 : 0.7),
                  }}
                >
                  <td style={{ padding:"8px 6px", verticalAlign:"middle" }}>
                    <div style={{
                      display:"flex", flexDirection:"row", flexWrap:"nowrap", alignItems:"stretch",
                      gap:6, minWidth:0,
                    }}>
                      <span style={{ color:D.faint, fontSize:14, flexShrink:0, alignSelf:"center", lineHeight:1 }}>⠿</span>
                      <div style={{
                        flex:"1 1 0%",
                        minWidth:72,
                        width:0,
                        height:112,
                        borderRadius:6,
                        overflow:"hidden",
                        background:D.surface2,
                      }}>
                        {p.foto ? (
                          <img
                            src={p.foto}
                            alt=""
                            style={{
                              width:"100%",
                              height:"100%",
                              display:"block",
                              objectFit:"cover",
                              objectPosition:"center",
                            }}
                          />
                        ) : (
                          <div style={{ width:"100%", height:"100%" }} />
                        )}
                      </div>
                      <div style={{ flex:"0 1 30%", minWidth:0, alignSelf:"center" }}>
                        <div
                          title={p.ref}
                          style={{
                            fontWeight:600, color:"#60a5fa", fontSize:11,
                            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                          }}
                        >{p.ref}</div>
                        {p.vendida   && <div style={{ fontSize:9,color:D.red,fontWeight:700, whiteSpace:"nowrap" }}>VENDIDA</div>}
                        {p.exclusiva && <div style={{ fontSize:9,color:"#f59e0b",fontWeight:700, whiteSpace:"nowrap" }}>EXCLUSIVA</div>}
                      </div>
                    </div>
                  </td>
                  <td
                    title={p.tipo}
                    style={{
                      padding:"10px 8px", fontSize:13, lineHeight:1.35, verticalAlign:"middle",
                      whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                    }}
                  >{p.tipo}</td>
                  <td
                    title={p.loc}
                    style={{
                      padding:"10px 8px", fontSize:13, lineHeight:1.35, verticalAlign:"middle",
                      whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                    }}
                  >{p.loc}</td>
                  <td
                    title={`${p.op} ${fmtP(p.precio, p.op)}`}
                    style={{
                      padding:"10px 6px 10px 8px", textAlign:"right", fontSize:13, lineHeight:1.35, verticalAlign:"middle",
                      whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                    }}
                  >
                    <span style={{ fontSize:10, fontWeight:700, marginRight:5, color:p.op==="ALQUILER"?"#4ade80":"#f87171" }}>{p.op}</span>
                    <span style={{ fontWeight:700 }}>{fmtP(p.precio,p.op)}</span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign:"center",padding:24,color:D.muted,fontSize:12 }}>
                  {cargando ? "Cargando propiedades…" : "Sin resultados"}
                </td></tr>
              )}
            </tbody>
          </table>
          {totalPags > 1 && (
            <div style={{ display:"flex", gap:6, justifyContent:"center", padding:10, flexWrap:"wrap" }}>
              {Array.from({ length: Math.min(totalPags,12) }, (_, i) => i+1).map(p => (
                <button key={p} onClick={() => cargar(p, buscar)} style={{
                  width:28, height:28, borderRadius:5, fontSize:11, fontWeight:600, border:"none",
                  background: p === pagina ? D.accent : D.btnGhostInactive,
                  color: p === pagina ? "#fff" : D.muted, cursor:"pointer",
                }}>{p}</button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* ══════════════ DERECHA — escaparate ══════════════ */}
      <div style={{ flex:1, display:"flex", overflow:"hidden", background:D.bg }}>

        {/* Sidebar */}
        <div style={{
          width:60, flexShrink:0, display:"flex", flexDirection:"column",
          alignItems:"center", padding:"14px 4px", gap:7,
          borderRight:"1px solid " + D.border, background:D.surface,
        }}>
          {(veVistaExt || veVistaInt) && (
          <div style={{ fontSize:8,color:D.faint,letterSpacing:1,textTransform:"uppercase",marginBottom:2 }}>Vista</div>
          )}
          {veVistaExt && (
          <SBtn active={vista==="exterior"} onClick={() => setVista("exterior")} title="Vista exterior (calle)">
            <span style={{ fontSize:15 }}>🪟</span>EXT
          </SBtn>
          )}
          {veVistaInt && (
          <SBtn active={vista==="interior"} onClick={() => setVista("interior")} title="Vista interior (oficina)">
            <span style={{ fontSize:15 }}>🏢</span>INT
          </SBtn>
          )}
          {veImprimir && (
          <>
          <div style={{ width:40,height:1,background:D.border,margin:"4px 0" }} />
          <div style={{ fontSize:8,color:D.faint,letterSpacing:1,textTransform:"uppercase" }}>Sel.</div>
          <SBtn onClick={selAll}  title="Seleccionar todos los que tienen propiedad"><span style={{ fontSize:14 }}>☑</span>Todos</SBtn>
          <SBtn onClick={selNone} title="Deseleccionar todos"><span style={{ fontSize:14 }}>☐</span>Ninguno</SBtn>
          <div style={{ width:40,height:1,background:D.border,margin:"4px 0" }} />
          <SBtn
            active={selCount > 0 && !printing} color={D.green}
            onClick={handlePrint}
            title={selCount > 0 ? "Imprimir " + selCount + " cartel(es) A3" : "Selecciona paneles para imprimir"}
          >
            <span style={{ fontSize:15 }}>{printing ? "⏳" : "🖨️"}</span>
            {selCount > 0 ? selCount : "—"}
          </SBtn>
          </>
          )}
          <div style={{ flex:1 }} />
          {veImprimir && (
          <div style={{ fontSize:8,color:D.faint,textAlign:"center",lineHeight:1.5 }}>
            {selCount} sel.<br />de 12
          </div>
          )}
        </div>

        {/* Zona vitrina */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", minHeight:0 }}>
          {/* Cabecera */}
          <div style={{
            padding:"9px 14px", borderBottom:"1px solid " + D.border,
            background:D.surface2, flexShrink:0,
            display:"flex", alignItems:"center", justifyContent:"space-between",
          }}>
            <span style={{ fontWeight:700, fontSize:13 }}>
              Gestión del Escaparate
              <span style={{ fontSize:10, color:D.muted, fontWeight:400, marginLeft:10 }}>
                {vista === "exterior" ? "🪟 Vista exterior (calle)" : "🏢 Vista interior (oficina)"} · 3×4 · único para todos los usuarios
              </span>
            </span>
            <span style={{ fontSize:10, color: selCount > 0 ? "#4ade80" : D.faint, fontWeight:600 }}>
              {selCount > 0 ? selCount + " sel." : "ninguno"}
            </span>
          </div>

          {/* ───── ESCAPARATE FÍSICO ───── */}
          <div style={{
            flex:1, minHeight:0,
            background:D.escOuter,
            padding:"6px 8px 8px",
            display:"flex", flexDirection:"column",
          }}>

            {/* Carril superior */}
            <div style={{
              height:22, flexShrink:0,
              background:D.escRailTop,
              borderRadius:"4px 4px 0 0",
              position:"relative",
              boxShadow:D.escRailTopShadow,
            }}>
              {/* Tira de luz LED */}
              <div style={{
                position:"absolute", bottom:2, left:6, right:6, height:3,
                background:D.escLed,
                borderRadius:2,
              }} />
              {/* Tornillos de sujeción de cables (3 por columna => 6 total) */}
              {[1,2,3,4,5,6].map(i => (
                <div key={i} style={{
                  position:"absolute", bottom:1,
                  left: (i / 7 * 100) + "%",
                  transform:"translateX(-50%)",
                  width:6, height:6, borderRadius:"50%",
                  background:"linear-gradient(135deg,#8899cc,#445577)",
                  border:"1px solid #667799", boxShadow:"0 1px 3px rgba(0,0,0,0.5)",
                }} />
              ))}
            </div>

            {/* Zona de paneles con cables */}
            <div style={{
              flex:1, minHeight:0,
              position:"relative",
              overflow:"hidden",
              background:D.escPanel,
              borderLeft:"4px solid " + D.escSideBorder,
              borderRight:"4px solid " + D.escSideBorder,
            }}>
              {/* 3 cables verticales (centrados en cada columna del grid) */}
              {[16.67, 50, 83.33].map((pct, ci) => (
                <div key={ci} style={{
                  position:"absolute",
                  top:0, bottom:0,
                  left: pct + "%",
                  transform:"translateX(-50%)",
                  width:2,
                  background:D.escCable,
                  opacity:0.65,
                  zIndex:0,
                  boxShadow:"0 0 4px rgba(100,130,200,0.2)",
                }} />
              ))}

              {/* Grid de paneles */}
              <div style={{
                position:"relative", zIndex:1,
                height:"100%", padding:"0 6px",
                display:"grid",
                gridTemplateColumns:"repeat(3,1fr)",
                gridTemplateRows:"repeat(4,1fr)",
                gap:"4px 12px",
              }}>
                {cur.map(panel => (
                  <EscPanel key={panel.id} panel={panel}
                    onCheck={handleCheck}
                    onEdit={id => setModal({ id })}
                    onDrop={handleDrop}
                    onZoomEnter={openZoomPreview}
                    onZoomLeave={scheduleHideZoom}
                    veAsignar={veAsignar}
                    modAsignar={modAsignar}
                    veImprimir={veImprimir}
                  />
                ))}
              </div>
            </div>

            {/* Carril inferior */}
            <div style={{
              height:14, flexShrink:0,
              background:D.escRailBot,
              borderRadius:"0 0 4px 4px",
              boxShadow:D.escRailBotShadow,
            }} />

          </div>
          {/* fin escaparate físico */}
        </div>
      </div>

      {modal && (
        <ModalRef
          panelId={modal.id}
          current={cur[modal.id]?.prop}
          allProps={propiedades}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {zoomPortal}
    </div>
  );
}
